import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { ModuleHeader, Kpi } from '@/components/datos/ModuleHeader';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { dinero, numero } from '@/lib/formato-tienda';
import { cn } from '@/lib/cn';
import type { Periodo } from '@/api/tienda.api';
import * as api from '@/api/seguimiento.api';
import type { FilaDeEficiencia } from '@/api/seguimiento.api';

/**
 * EFICIENCIA — qué anuncio, qué vendedora y qué sucursal venden de verdad.
 *
 * El embudo: chats nuevos → pasaron a vendedora → compraron. Al lado, las ventas confirmadas por
 * el sistema y lo vendido. Con el costo de cada anuncio anotado, sale el costo por venta y el
 * retorno. Donde no hay dato sale «—», nunca un cero inventado.
 */

const PERIODOS: Array<{ id: Periodo; etiqueta: string }> = [
  { id: 'hoy', etiqueta: 'Hoy' },
  { id: 'semana', etiqueta: 'Esta semana' },
  { id: 'mes', etiqueta: 'Este mes' },
];

const VISTAS = [
  { id: 'porAnuncio', etiqueta: 'Por anuncio' },
  { id: 'porVendedora', etiqueta: 'Por vendedora' },
  { id: 'porSucursal', etiqueta: 'Por sucursal' },
] as const;
type Vista = (typeof VISTAS)[number]['id'];

const pct = (n: number | null) => (n == null ? '—' : `${n} %`);
const veces = (n: number | null) => (n == null ? '—' : `${n.toLocaleString('es-DO')}×`);

function Tabla({
  filas,
  conCosto,
  onCosto,
}: {
  filas: FilaDeEficiencia[];
  conCosto: boolean;
  onCosto?: (f: FilaDeEficiencia) => void;
}) {
  if (filas.length === 0) {
    return <p className="py-8 text-center text-sm text-slate-400">Sin movimiento en este período.</p>;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-surface-border">
      <table className="w-full min-w-[720px] text-sm">
        <thead className="bg-slate-50 text-left text-xs text-slate-500 dark:bg-slate-900">
          <tr>
            <th className="px-3 py-2 font-medium"> </th>
            <th className="px-3 py-2 text-right font-medium">Chats</th>
            <th className="px-3 py-2 text-right font-medium">Pasaron</th>
            <th className="px-3 py-2 text-right font-medium">Compraron</th>
            <th className="px-3 py-2 text-right font-medium">No compraron</th>
            <th className="px-3 py-2 text-right font-medium">Por cerrar</th>
            <th className="px-3 py-2 text-right font-medium">Cierre</th>
            <th className="px-3 py-2 text-right font-medium">Ventas</th>
            <th className="px-3 py-2 text-right font-medium">Vendido</th>
            {conCosto && (
              <>
                <th className="px-3 py-2 text-right font-medium">Costo</th>
                <th className="px-3 py-2 text-right font-medium">Por venta</th>
                <th className="px-3 py-2 text-right font-medium">Retorno</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => (
            <tr key={f.clave} className="border-t border-surface-border dark:border-slate-800">
              <td className="max-w-[220px] truncate px-3 py-2 font-medium text-slate-800 dark:text-slate-100" title={f.nombre}>
                {f.nombre}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{numero(f.chats)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{numero(f.pasaron)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-green-700 dark:text-green-300">{numero(f.compraron)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{numero(f.noCompraron)}</td>
              <td className={cn('px-3 py-2 text-right tabular-nums', f.vencidos > 0 && 'text-red-600 dark:text-red-400')}>
                {numero(f.pendientes)}
                {f.vencidos > 0 ? ` (${f.vencidos} tarde)` : ''}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{pct(f.tasaDeCierre)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{numero(f.ventas)}</td>
              <td className="px-3 py-2 text-right font-medium tabular-nums">{dinero(f.vendido)}</td>
              {conCosto && (
                <>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {f.clave === '__sin__' ? (
                      '—'
                    ) : (
                      <button
                        type="button"
                        onClick={() => onCosto?.(f)}
                        className="inline-flex items-center gap-1 text-brand-700 underline-offset-2 hover:underline dark:text-brand-300"
                      >
                        {f.costo == null ? 'Anotar' : dinero(f.costo)}
                        <Pencil className="h-3 w-3" />
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{dinero(f.costoPorVenta)}</td>
                  <td
                    className={cn(
                      'px-3 py-2 text-right tabular-nums',
                      f.retorno != null && (f.retorno >= 1 ? 'text-green-700 dark:text-green-300' : 'text-red-600 dark:text-red-400'),
                    )}
                  >
                    {veces(f.retorno)}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Eficiencia() {
  const [periodo, setPeriodo] = useState<Periodo>('mes');
  const [vista, setVista] = useState<Vista>('porAnuncio');
  const [editando, setEditando] = useState<FilaDeEficiencia | null>(null);
  const [costo, setCosto] = useState('');
  const toast = useToast();
  const qc = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['eficiencia', periodo],
    queryFn: () => api.eficiencia(periodo),
  });

  const guardar = useMutation({
    mutationFn: () =>
      api.ponerCosto({ sourceId: editando!.clave, titulo: editando!.nombre, costo: Math.max(0, Math.round(Number(costo) || 0)) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['eficiencia'] });
      setEditando(null);
      toast.success('Costo del anuncio guardado');
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo guardar'),
  });

  const t = data?.total;

  return (
    <div className="space-y-5">
      <ModuleHeader
        titulo="Eficiencia"
        subtitulo="De cada anuncio, vendedora y sucursal: cuántos escribieron, cuántos compraron y cuánto se vendió."
        acciones={
          <div className="flex rounded-full border border-surface-border bg-surface p-0.5">
            {PERIODOS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPeriodo(p.id)}
                aria-pressed={periodo === p.id}
                className={cn(
                  'rounded-full px-3 py-1.5 text-sm transition-colors',
                  periodo === p.id ? 'bg-brand-600 font-medium text-white' : 'text-slate-600 hover:text-slate-900 dark:text-slate-300',
                )}
              >
                {p.etiqueta}
              </button>
            ))}
          </div>
        }
        kpis={
          t && (
            <>
              <Kpi titulo="Chats nuevos" valor={numero(t.chats)} pie="escribieron en el período" />
              <Kpi titulo="Pasaron a vendedora" valor={numero(t.pasaron)} pie={`${numero(t.pendientes)} por cerrar`} />
              <Kpi titulo="Compraron" valor={numero(t.compraron)} pie={`cierre ${pct(t.tasaDeCierre)}`} tono="bueno" />
              <Kpi titulo="Vendido por el sistema" valor={dinero(t.vendido)} pie={`${numero(t.ventas)} ventas confirmadas`} />
            </>
          )
        }
      />

      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Vista">
        {VISTAS.map((v) => (
          <button
            key={v.id}
            type="button"
            role="tab"
            aria-selected={vista === v.id}
            onClick={() => setVista(v.id)}
            className={cn(
              'rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
              vista === v.id
                ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300',
            )}
          >
            {v.etiqueta}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size={28} />
        </div>
      ) : isError || !data ? (
        <div className="space-y-3 py-10 text-center">
          <p className="text-sm text-slate-600 dark:text-slate-300">No se pudo cargar la eficiencia.</p>
          <Button variant="outline" onClick={() => void refetch()}>
            Reintentar
          </Button>
        </div>
      ) : (
        <Tabla
          filas={data[vista]}
          conCosto={vista === 'porAnuncio'}
          onCosto={(f) => {
            setEditando(f);
            setCosto(f.costo == null ? '' : String(f.costo));
          }}
        />
      )}

      <p className="text-xs text-slate-400">
        Chats y seguimientos cuentan por el día en que empezaron; las ventas, por el día en que se confirmaron. «Pasaron» son los
        que quedaron en comprar (los pasó el asistente, los marcó la vendedora o quedó una venta en borrador). El retorno es lo
        vendido entre lo que costó el anuncio.
      </p>

      <Modal
        open={!!editando}
        onClose={() => setEditando(null)}
        title="Cuánto costó este anuncio"
        description={editando?.nombre}
        footer={
          <>
            <Button variant="outline" onClick={() => setEditando(null)}>
              Cancelar
            </Button>
            <Button onClick={() => guardar.mutate()} loading={guardar.isPending}>
              Guardar
            </Button>
          </>
        }
      >
        <label className="block space-y-1">
          <span className="text-xs text-slate-500">Lo invertido en este anuncio, en pesos (RD$)</span>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            value={costo}
            onChange={(e) => setCosto(e.target.value)}
            className="w-full rounded-md border border-surface-border bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
          />
        </label>
      </Modal>
    </div>
  );
}
